import { computed, Injectable, signal } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { selectLanguaje } from '@store/selectors';
import { ComponentEnum, LanguajeEnum } from '@core/enums';
import { I18nService } from '@core/services';
import { IAbstractPresenter, ILanguageObject } from '@core/interfaces';
import { Observable, Subject, takeUntil } from 'rxjs';
import { IAuthModalCallback, IAuthModalComponent, IAuthModalPresenter } from './interfaces';
import { AuthModalFactory } from './domain/auth-modal.factory';
import { AuthModalComponentEnum } from './enums';
import { IBaseStrategy } from '@core/interfaces/base-strategy';

@Injectable({
	providedIn: 'root',
})
export class AuthModalPresenter extends IAbstractPresenter<IAuthModalComponent> implements IAuthModalPresenter {
	componentIdSignal = signal<AuthModalComponentEnum>(AuthModalComponentEnum.LOGIN);
	translations: ILanguageObject = {};
	visitingCheckout: boolean = false;

	languageEnum$: Observable<LanguajeEnum>;
	selectedComponent = computed(() => {
		return this.authModalFactory.getStrategy(this.componentIdSignal());
	});

	private translationsSignal = signal<ILanguageObject>({});
	dialogTitle = computed(() => {
		const key = this.componentIdSignal();
		return this.translationsSignal()[key] ?? key;
	});

	strategy: IBaseStrategy<IAuthModalCallback> = {
		callback: {
			navigate: (action: AuthModalComponentEnum): void => this.handleStrategyAction(action),
			navigateGuest: () => this.onContinueAsGuest(),
		},
	};

	constructor(
		private modalCtrl: ModalController,
		private store: Store,
		private i18nService: I18nService,
		private authModalFactory: AuthModalFactory
	) {
		super();

		this.languageEnum$ = this.store.select(selectLanguaje);
	}

	override onInit(): void {
		this.destroy$ = new Subject<void>();
		const initial = this.view?.initialComponent ?? AuthModalComponentEnum.LOGIN;
		this.componentIdSignal.set(initial);
	}

	override onDestroy(): void {
		this.componentIdSignal.set(AuthModalComponentEnum.LOGIN);
		super.onDestroy();
	}

	dispatchActions(): void {}

	suscribeObservers(): void {
		this.languageEnum$.pipe(takeUntil(this.destroy$)).subscribe(lang => {
			const t = this.i18nService.getTranslations(ComponentEnum.Auth, lang);
			this.translations = t;
			this.view.translations = t;
			// Actualizar el signal para que dialogTitle() recompute automáticamente
			this.translationsSignal.set(t);
		});
	}

	onClose(): void {
		this.componentIdSignal.set(AuthModalComponentEnum.LOGIN);
		this.modalCtrl.dismiss();
	}

	onContinueAsGuest(): void {
		this.componentIdSignal.set(AuthModalComponentEnum.LOGIN);
		this.modalCtrl.dismiss('continueAsGuest');
	}

	handleStrategyAction(action: AuthModalComponentEnum): void {
		this.componentIdSignal.set(action);
	}
}
